import React, { useCallback, useState } from "react";
import axios from "axios";
import Hoot from "./Hoot";
import { toast } from "react-toastify";
import styled, { createGlobalStyle, keyframes, css } from "styled-components";
import { Modal, Spinner, Row, Col, Form, Table } from "react-bootstrap";
import {
    TbLicense,
    TbSchool,
    TbBuilding,
    TbPhone,
    TbUserCircle,
    TbId,
    TbCalendar,
    TbCoin,
    TbReceipt,
    TbPlus,
    TbEdit,
    TbTrash,
    TbRefresh,
    TbAlertTriangle,
    TbInfoCircle,
    TbClockHour4,
    TbX,
    TbDeviceFloppy,
    TbCircleCheck,
    TbHourglassHigh,
    TbCircleX,
    TbUsersGroup,
} from "react-icons/tb";

// ============================================================
// Helpers puros
// ============================================================

function safeNumber(value, fallback = 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
}

function formatDate(value) {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" });
}

function daysUntil(value) {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return Math.ceil((date.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

function getLicenseStatus(endDate) {
    const remaining = daysUntil(endDate);
    if (remaining === null) return "unknown";
    if (remaining < 0) return "expired";
    if (remaining <= 30) return "soon";
    return "active";
}

function getInitials(name) {
    if (!name || typeof name !== "string") return "?";
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "?";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function extractErrorMessage(error, fallback) {
    const backendMessage = error && error.response && error.response.data && error.response.data.message;
    if (typeof backendMessage === "string" && backendMessage.trim().length > 0) return backendMessage;
    return fallback;
}

function normalizeContactsResponse(payload) {
    if (payload && Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload)) return payload;
    return [];
}

const EMPTY_CONTACT_FORM = { nome: "", telefone: "", cargo: "" };

const STATUS_META = {
    active: { label: "Licença ativa", icon: TbCircleCheck, tone: "success" },
    soon: { label: "A expirar em breve", icon: TbHourglassHigh, tone: "warning" },
    expired: { label: "Licença expirada", icon: TbCircleX, tone: "danger" },
    unknown: { label: "Sem data definida", icon: TbInfoCircle, tone: "neutral" },
};

// ============================================================
// DESIGN TOKENS — tema claro / "UI limpa", acento roxo #6051FF
// ============================================================

const tokens = {
    ink: "#1A1B25",
    inkSoft: "#43465A",
    inkMuted: "#7A7D8E",
    surface: "#FFFFFF",
    canvas: "#FBFBFE",
    canvasDeep: "#F3F2FC",
    primary: "#6051FF",
    primaryDeep: "#4B3DE0",
    primarySoft: "#EEECFF",
    primarySofter: "#F6F5FF",
    sage: "#1F9D57",
    sageSoft: "#E4F8EC",
    rust: "#E5484D",
    rustSoft: "#FDEBEC",
    amber: "#B5790A",
    amberSoft: "#FCF1DA",
    neutral: "#8A8D9B",
    neutralSoft: "#F1F1F6",
    line: "#E9E8F3",
};

const toneColors = {
    success: { fg: tokens.sage, bg: tokens.sageSoft },
    warning: { fg: tokens.amber, bg: tokens.amberSoft },
    danger: { fg: tokens.rust, bg: tokens.rustSoft },
    neutral: { fg: tokens.neutral, bg: tokens.neutralSoft },
};

const GlobalFonts = createGlobalStyle`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@500;600;700&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap');

    .slacm-scope {
        font-family: "Inter", system-ui, sans-serif;
        color: ${tokens.ink};
    }
`;

const fadeUp = keyframes`
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
    to { transform: rotate(360deg); }
`;

// ============================================================
// STYLED COMPONENTS
// ============================================================

const TriggerButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    border-radius: 10px;
    border: none;
    background:#FFB703;
    color: #fff; 
    transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;

    svg { font-size: 19px; }

    &:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 6px 16px rgba(96, 81, 255, 0.4);
        filter: brightness(1.05);
    }
    &:active:not(:disabled) { transform: translateY(0); }
    &:disabled {
        background: ${tokens.neutralSoft};
        color: ${tokens.neutral};
        box-shadow: none;
        cursor: not-allowed;
    }
    &:focus-visible {
        outline: 2px solid ${tokens.primaryDeep};
        outline-offset: 2px;
    }
`;

const StyledModal = styled(Modal)`
    .modal-content {
        border: 1px solid ${tokens.line};
        border-radius: 18px;
        overflow: hidden;
        box-shadow: 0 24px 60px rgba(26, 27, 37, 0.14);
        background: ${tokens.surface};
    }
    .modal-header {
        background: ${tokens.surface};
        border-bottom: 1px solid ${tokens.line};
        padding: 20px 24px;
        color: ${tokens.ink};
    }
    .modal-title {
        font-family: "Plus Jakarta Sans", sans-serif;
        font-weight: 700;
        font-size: 20px;
        display: flex;
        align-items: center;
        gap: 10px;

        svg { color: ${tokens.primary}; }
    }
    .btn-close {
        opacity: 0.55;
        &:hover { opacity: 0.9; }
    }
    .modal-body {
        padding: 22px 24px;
        background: ${tokens.canvas};
    }
    .modal-footer {
        border-top: 1px solid ${tokens.line};
        background: ${tokens.surface};
        padding: 14px 24px;
    }
`;

const SectionCard = styled.section`
    position: relative;
    background: ${tokens.surface};
    border: 1px solid ${tokens.line};
    border-radius: 14px;
    padding: 20px 22px;
    margin-bottom: 16px;
    animation: ${fadeUp} 0.22s ease both;
    box-shadow: 0 1px 2px rgba(26, 27, 37, 0.03);
    transition: box-shadow 0.2s ease, border-color 0.2s ease;

    &::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        border-radius: 14px 14px 0 0;
        background: ${(p) =>
            p.$accent === "license" ? tokens.primary : p.$accent === "contacts" ? tokens.sage : tokens.inkSoft};
    }

    &:hover {
        box-shadow: 0 8px 22px rgba(96, 81, 255, 0.08);
        border-color: ${tokens.primarySoft};
    }

    &:last-child { margin-bottom: 0; }
`;

const SectionHead = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 16px;
    flex-wrap: wrap;
`;

const SectionTitle = styled.h3`
    font-family: "Plus Jakarta Sans", sans-serif;
    font-weight: 700;
    font-size: 16px;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 9px;
    color: ${tokens.ink};

    svg { color: ${(p) => p.$iconColor || tokens.inkSoft}; font-size: 18px; }
`;

const Pill = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 12px;
    border-radius: 100px;
    font-size: 12.5px;
    font-weight: 600;
    letter-spacing: 0.01em;
    color: ${(p) => toneColors[p.$tone || "neutral"].fg};
    background: ${(p) => toneColors[p.$tone || "neutral"].bg};
    white-space: nowrap;

    svg { font-size: 14px; }
`;

const TypeBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 12px;
    border-radius: 100px;
    font-size: 12.5px;
    font-weight: 600;
    color: ${(p) => (p.$university ? "#fff" : tokens.primaryDeep)};
    background: ${(p) => (p.$university ? tokens.primary : tokens.primarySoft)};
`;

const FieldGrid = styled(Row)`
    row-gap: 16px;
`;

const FieldLabel = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-family: "IBM Plex Mono", monospace;
    font-size: 10.5px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${tokens.inkMuted};
    margin-bottom: 3px;

    svg { font-size: 12.5px; }
`;

const FieldValue = styled.div`
    font-size: 14.5px;
    font-weight: 500;
    color: ${tokens.ink};
`;

const ProgressTrack = styled.div`
    position: relative;
    height: 8px;
    border-radius: 100px;
    background: ${tokens.canvasDeep};
    overflow: hidden;
    margin-bottom: 6px;
`;

const ProgressFill = styled.div`
    height: 100%;
    border-radius: 100px;
    width: ${(p) => Math.min(100, Math.max(0, p.$pct))}%;
    background: ${(p) => `linear-gradient(90deg, ${toneColors[p.$tone].fg}, ${toneColors[p.$tone].fg}CC)`};
    transition: width 0.4s ease;
`;

const ProgressCaption = styled.div`
    display: flex;
    justify-content: space-between;
    font-size: 12.5px;
    color: ${tokens.inkMuted};
    margin-bottom: 18px;

    svg { margin-right: 4px; vertical-align: -2px; }
`;

const ButtonBase = css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-family: "Inter", sans-serif;
    font-weight: 600;
    font-size: 13.5px;
    padding: 9px 16px;
    border-radius: 9px;
    border: 1.5px solid transparent;
    cursor: pointer;
    transition: transform 0.12s ease, filter 0.12s ease, box-shadow 0.12s ease;
    white-space: nowrap;

    &:hover:not(:disabled) { filter: brightness(1.05); transform: translateY(-1px); }
    &:active:not(:disabled) { transform: translateY(0); }
    &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
    &:focus-visible { outline: 2px solid ${tokens.primary}; outline-offset: 2px; }
`;

const PrimaryButton = styled.button`
    ${ButtonBase}
    background: ${tokens.primary};
    color: #fff;
    box-shadow: 0 2px 8px rgba(96, 81, 255, 0.3);
`;

const SuccessButton = styled.button`
    ${ButtonBase}
    background: ${tokens.sage};
    color: #fff;
    box-shadow: 0 2px 8px rgba(31, 157, 87, 0.25);
`;

const GhostButton = styled.button`
    ${ButtonBase}
    background: ${tokens.surface};
    border-color: ${tokens.line};
    color: ${tokens.ink};
`;

const DangerGhostButton = styled.button`
    ${ButtonBase}
    background: ${tokens.rustSoft};
    color: ${tokens.rust};
    border-color: ${tokens.rust}33;
    padding: 7px 10px;
`;

const EditGhostButton = styled.button`
    ${ButtonBase}
    background: ${tokens.primarySofter};
    color: ${tokens.primaryDeep};
    padding: 7px 10px;
`;

const AmountField = styled(Form.Control)`
    width: 84px;
    text-align: center;
    font-family: "IBM Plex Mono", monospace;
    border: 1.5px solid ${tokens.line};
    border-radius: 8px;

    &:focus {
        border-color: ${tokens.primary};
        box-shadow: 0 0 0 3px ${tokens.primarySoft};
    }
`;

const ActionsRow = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 10px;
    margin-top: 18px;
    padding-top: 18px;
    border-top: 1px dashed ${tokens.line};
    flex-wrap: wrap;
`;

const TableWrap = styled.div`
    border: 1px solid ${tokens.line};
    border-radius: 10px;
    overflow: hidden;
`;

const StyledTable = styled(Table)`
    margin-bottom: 0;

    thead th {
        font-family: "IBM Plex Mono", monospace;
        font-size: 10.5px;
        letter-spacing: 0.07em;
        text-transform: uppercase;
        color: ${tokens.inkMuted};
        font-weight: 500;
        background: ${tokens.canvasDeep};
        border-bottom: none;
        padding: 10px 14px;
    }
    tbody td {
        padding: 11px 14px;
        vertical-align: middle;
        border-color: ${tokens.line};
        font-size: 14px;
    }
    tbody tr:hover { background: ${tokens.primarySofter}; }
    tbody tr:last-child td { border-bottom: none; }
`;

const Avatar = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11.5px;
    font-weight: 700;
    color: #fff;
    background: linear-gradient(160deg, ${tokens.primary}, ${tokens.primaryDeep});
    margin-right: 10px;
    flex-shrink: 0;
`;

const ContactName = styled.span`
    display: inline-flex;
    align-items: center;
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 34px 16px;
    color: ${tokens.inkMuted};
    font-size: 14px;
    border: 1.5px dashed ${tokens.line};
    border-radius: 10px;
    background: ${tokens.canvas};

    svg { font-size: 26px; color: ${tokens.primary}; margin-bottom: 8px; }
`;

const LoadingBlock = styled.div`
    text-align: center;
    padding: 56px 16px;
    color: ${tokens.inkMuted};
`;

const RingSpinner = styled.div`
    width: 38px;
    height: 38px;
    margin: 0 auto 14px;
    border-radius: 50%;
    border: 3px solid ${tokens.canvasDeep};
    border-top-color: ${tokens.primary};
    animation: ${spin} 0.8s linear infinite;
`;

const ErrorBanner = styled.div`
    display: flex;
    gap: 12px;
    align-items: flex-start;
    padding: 16px 18px;
    border-radius: 10px;
    background: ${tokens.rustSoft};
    border: 1px solid ${tokens.rust}33;
    color: ${tokens.rust};

    svg { font-size: 20px; flex-shrink: 0; margin-top: 2px; }
`;

const ContactFormWrap = styled(Form)`
    border-top: 1px solid ${tokens.line};
    padding-top: 16px;
    margin-top: 16px;
    animation: ${fadeUp} 0.18s ease;
`;

const FormLabel = styled(Form.Label)`
    font-size: 11.5px;
    color: ${tokens.inkMuted};
    margin-bottom: 4px;
`;

const StyledInput = styled(Form.Control)`
    border: 1.5px solid ${tokens.line};
    border-radius: 8px;

    &:focus {
        border-color: ${tokens.primary};
        box-shadow: 0 0 0 3px ${tokens.primarySoft};
    }
`;

const FormActions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 14px;
`;

const FooterBar = styled.div`
    display: flex;
    justify-content: flex-end;
`;

// ============================================================
// COMPONENTE
// ============================================================

export default function SchoolLicenseAndContactsModal({ instituteCode, totalLicenDays }) {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(false);

    const [institute, setInstitute] = useState(null);

    const [contacts, setContacts] = useState([]);
    const [contactsLoading, setContactsLoading] = useState(false);

    const [periodAmount, setPeriodAmount] = useState(1);
    const [periodActionLoading, setPeriodActionLoading] = useState(null); // "increment" | "decrement" | null
    const [typeActionLoading, setTypeActionLoading] = useState(false);

    const [contactModalData, setContactModalData] = useState(null); // null | {} | contact
    const [contactForm, setContactForm] = useState(EMPTY_CONTACT_FORM);
    const [savingContact, setSavingContact] = useState(false);
    const [deletingContactId, setDeletingContactId] = useState(null);

    const isDisabled = !instituteCode;

    // ------------------------------------------------------------
    // Carregamento de dados
    // ------------------------------------------------------------

    const fetchInstitute = useCallback(async () => {
        try {
            const response = await axios.get(Hoot() + "institute/" + instituteCode);
            const data = response && response.data;
            if (!data || typeof data !== "object" || Array.isArray(data)) {
                throw new Error("Formato de resposta inesperado.");
            }
            setInstitute(data);
            return true;
        } catch (error) {
            console.error(error);
            setInstitute(null);
            const status = error && error.response && error.response.status;
            if (status === 404) {
                toast.warning("Instituição não encontrada.");
            } else if (status === 401 || status === 403) {
                toast.error("Sem permissão para aceder a estes dados.");
            } else {
                toast.error(extractErrorMessage(error, "Não foi possível carregar os dados da instituição."));
            }
            return false;
        }
    }, [instituteCode]);

    const fetchContacts = useCallback(async () => {
        setContactsLoading(true);
        try {
            const response = await axios.get(Hoot() + "api/schools/" + instituteCode + "/contacts");
            setContacts(normalizeContactsResponse(response && response.data));
        } catch (error) {
            console.error(error);
            setContacts([]);
            toast.error(extractErrorMessage(error, "Não foi possível carregar os contactos."));
        } finally {
            setContactsLoading(false);
        }
    }, [instituteCode]);

    const loadAll = useCallback(async () => {
        setLoading(true);
        setLoadError(false);
        try {
            const [instituteOk] = await Promise.all([fetchInstitute(), fetchContacts()]);
            if (!instituteOk) setLoadError(true);
        } finally {
            setLoading(false);
        }
    }, [fetchInstitute, fetchContacts]);

    // ------------------------------------------------------------
    // Abrir / fechar modal
    // ------------------------------------------------------------

    const handleOpen = () => {
        if (isDisabled || loading) return;
        setShow(true);
        loadAll();
    };

    const handleClose = () => {
        if (periodActionLoading || typeActionLoading || savingContact || deletingContactId) return;
        setShow(false);
        setInstitute(null);
        setContacts([]);
        setContactModalData(null);
        setContactForm(EMPTY_CONTACT_FORM);
        setPeriodAmount(1);
        setLoadError(false);
    };

    // ------------------------------------------------------------
    // Licença: incrementar / decrementar dias
    // Backend real: POST /api/schools/:schoolCode/license/increment|decrement
    // body: { days }, resposta: { data: { ed_institute_licence_endDate, ... } }
    // ------------------------------------------------------------

    const handleLicensePeriod = async (action) => {
        if (!institute) return;
        const amount = safeNumber(periodAmount, 0);
        if (amount <= 0) {
            toast.warning("Indique um número de dias válido.");
            return;
        }
        setPeriodActionLoading(action);
        try {
            const response = await axios.post(
                Hoot() + "api/schools/" + instituteCode + "/license/" + action,
                { days: amount }
            );
            const payload = response && response.data;
            const newEndDate = payload && payload.data && payload.data.ed_institute_licence_endDate;
            if (newEndDate) {
                setInstitute((prev) => (prev ? { ...prev, ed_institute_licence_endDate: newEndDate } : prev));
            }
            toast.success((payload && payload.message) || "Período da licença atualizado.");
        } catch (error) {
            console.error(error);
            toast.error(extractErrorMessage(error, "Não foi possível atualizar o período da licença."));
        } finally {
            setPeriodActionLoading(null);
        }
    };

    // ------------------------------------------------------------
    // Instituição: trocar tipo (escola / universidade)
    // Backend real: PATCH /api/schools/:schoolCode/type
    // body: { schoolType: "escola" | "universidade" } (0 = escola, 1 = universidade)
    // resposta: { data: { ed_institute_type, ... } }
    // ------------------------------------------------------------

    const handleToggleType = async () => {
        if (!institute) return;
        const isCurrentlyUniversity = institute.ed_institute_type === 1;
        const newSchoolType = isCurrentlyUniversity ? "escola" : "universidade";
        setTypeActionLoading(true);
        try {
            const response = await axios.patch(
                Hoot() + "api/schools/" + instituteCode + "/type",
                { schoolType: newSchoolType }
            );
            const payload = response && response.data;
            const resolvedType =
                payload && payload.data && typeof payload.data.ed_institute_type === "number"
                    ? payload.data.ed_institute_type
                    : newSchoolType === "universidade"
                    ? 1
                    : 0;
            setInstitute((prev) => (prev ? { ...prev, ed_institute_type: resolvedType } : prev));
            toast.success((payload && payload.message) || "Tipo de instituição atualizado.");
        } catch (error) {
            console.error(error);
            toast.error(extractErrorMessage(error, "Não foi possível atualizar o tipo de instituição."));
        } finally {
            setTypeActionLoading(false);
        }
    };

    // ------------------------------------------------------------
    // Contactos: criar / editar / remover
    // ------------------------------------------------------------

    const openNewContact = () => {
        setContactModalData({});
        setContactForm(EMPTY_CONTACT_FORM);
    };

    const openEditContact = (contact) => {
        setContactModalData(contact);
        setContactForm({
            nome: contact && contact.nome ? contact.nome : "",
            telefone: contact && contact.telefone ? contact.telefone : "",
            cargo: contact && contact.cargo ? contact.cargo : "",
        });
    };

    const closeContactForm = () => {
        if (savingContact) return;
        setContactModalData(null);
        setContactForm(EMPTY_CONTACT_FORM);
    };

    const handleContactFieldChange = (field, value) => {
        setContactForm((prev) => ({ ...prev, [field]: value }));
    };

    const submitContact = async (event) => {
        event.preventDefault();

        const nome = (contactForm.nome || "").trim();
        const cargo = (contactForm.cargo || "").trim();
        if (!nome || !cargo) {
            toast.warning("Nome e cargo são obrigatórios.");
            return;
        }

        const isEdit = Boolean(contactModalData && contactModalData.id);
        const payload = { nome, cargo, telefone: (contactForm.telefone || "").trim() || null };

        setSavingContact(true);
        try {
            const response = isEdit
                ? await axios.put(
                      Hoot() + "api/schools/" + instituteCode + "/contacts/" + contactModalData.id,
                      payload
                  )
                : await axios.post(Hoot() + "api/schools/" + instituteCode + "/contacts", payload);

            toast.success(
                (response && response.data && response.data.message) ||
                    (isEdit ? "Contacto atualizado com sucesso." : "Contacto criado com sucesso.")
            );
            setContactModalData(null);
            setContactForm(EMPTY_CONTACT_FORM);
            await fetchContacts();
        } catch (error) {
            console.error(error);
            toast.error(extractErrorMessage(error, "Não foi possível guardar o contacto."));
        } finally {
            setSavingContact(false);
        }
    };

    const handleDeleteContact = async (contact) => {
        if (!contact || !contact.id) return;
        setDeletingContactId(contact.id);
        try {
            const response = await axios.delete(
                Hoot() + "api/schools/" + instituteCode + "/contacts/" + contact.id
            );
            toast.success((response && response.data && response.data.message) || "Contacto removido.");
            await fetchContacts();
        } catch (error) {
            console.error(error);
            toast.error(extractErrorMessage(error, "Não foi possível remover o contacto."));
        } finally {
            setDeletingContactId(null);
        }
    };

    // ------------------------------------------------------------
    // Derivados de apresentação
    // ------------------------------------------------------------

    const licenseStatus = institute ? getLicenseStatus(institute.ed_institute_licence_endDate) : "unknown";
    const statusMeta = STATUS_META[licenseStatus];
    const StatusIcon = statusMeta.icon;
    const remainingDays = institute ? daysUntil(institute.ed_institute_licence_endDate) : null;
    const totalDays = safeNumber(totalLicenDays, 0);
    const progressPct =
        totalDays > 0 && remainingDays !== null ? Math.min(100, Math.max(0, (remainingDays / totalDays) * 100)) : 0;
    const isUniversity = institute && institute.ed_institute_type === 1;
    const anyActionBusy =
        Boolean(periodActionLoading) || typeActionLoading || savingContact || Boolean(deletingContactId);

    // ------------------------------------------------------------
    // Render
    // ------------------------------------------------------------

    return (
        <div className="slacm-scope">
            <GlobalFonts />

            <TriggerButton
                type="button"
                disabled={isDisabled}
                onClick={handleOpen}
                title="Licença e contactos da escola"
            >
                <TbLicense />
            </TriggerButton>

            <StyledModal show={show} onHide={handleClose} size="lg" centered scrollable>
                <Modal.Header closeButton={!anyActionBusy}>
                    <Modal.Title>
                        <TbLicense />
                        <span>Licença e contactos</span>
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {loading && (
                        <LoadingBlock>
                            <RingSpinner />
                            <div>A carregar dados da instituição...</div>
                        </LoadingBlock>
                    )}

                    {!loading && loadError && (
                        <ErrorBanner>
                            <TbAlertTriangle />
                            <div>
                                <div>Não foi possível carregar os dados desta instituição.</div>
                                <GhostButton type="button" style={{ marginTop: 10 }} onClick={loadAll}>
                                    <TbRefresh />
                                    Tentar novamente
                                </GhostButton>
                            </div>
                        </ErrorBanner>
                    )}

                    {!loading && !loadError && institute && (
                        <>
                            {/* -------- Instituição -------- */}
                            <SectionCard $accent="institute">
                                <SectionHead>
                                    <SectionTitle>
                                        <TbBuilding />
                                        {institute.ed_institute_name || institute.ed_institute_code || "Instituição"}
                                    </SectionTitle>
                                    <TypeBadge $university={isUniversity}>
                                        <TbSchool />
                                        {isUniversity ? "Universidade" : "Escola"}
                                    </TypeBadge>
                                </SectionHead>

                                <FieldGrid>
                                    <Col xs={6} md={4}>
                                        <FieldLabel>
                                            <TbId />
                                            Código
                                        </FieldLabel>
                                       <input type="text" value={institute.ed_institute_code || "-"}  className="form-control" />
                                    </Col>
                                    <Col xs={6} md={4}>
                                        <FieldLabel>
                                            <TbId /> ID interno
                                        </FieldLabel>
                                        <input type="text" value={institute.ed_institute_id != null ? institute.ed_institute_id : "-"} className="form-control" />
                                    </Col>
                                </FieldGrid>

                                <ActionsRow>
                                    <GhostButton type="button" disabled={anyActionBusy} onClick={handleToggleType}>
                                        {typeActionLoading ? <Spinner animation="border" size="sm" /> : <TbSchool />}
                                        Mudar para {isUniversity ? "Escola" : "Universidade"}
                                    </GhostButton>
                                </ActionsRow>
                            </SectionCard>

                            {/* -------- Licença -------- */}
                            <SectionCard $accent="license">
                                <SectionHead>
                                    <SectionTitle $iconColor={tokens.primary}>
                                        <TbReceipt />
                                        Licença
                                    </SectionTitle>
                                    <Pill $tone={statusMeta.tone}>
                                        <StatusIcon />
                                        {statusMeta.label}
                                    </Pill>
                                </SectionHead>

                                <ProgressTrack>
                                    <ProgressFill $pct={progressPct} $tone={statusMeta.tone} />
                                </ProgressTrack>
                                <ProgressCaption>
                                    <span>
                                        <TbClockHour4 />
                                        {remainingDays !== null
                                            ? remainingDays >= 0
                                                ? `${remainingDays} dia(s) restante(s)`
                                                : `${Math.abs(remainingDays)} dia(s) em atraso`
                                            : "Sem data de fim"}
                                    </span>
                                    {totalDays > 0 && <span>de {totalDays} dias no total</span>}
                                </ProgressCaption>

                                <FieldGrid>
                                    <Col xs={6} md={4}>
                                        <FieldLabel>
                                            <TbCalendar />
                                            Início
                                        </FieldLabel>
                                        <FieldValue>{formatDate(institute.ed_institute_licence_startDate)}</FieldValue>
                                    </Col>
                                    <Col xs={6} md={4}>
                                        <FieldLabel>
                                            <TbCalendar />
                                            Fim
                                        </FieldLabel>
                                        <FieldValue>{formatDate(institute.ed_institute_licence_endDate)}</FieldValue>
                                    </Col>
                                    <Col xs={6} md={4}>
                                        <FieldLabel>
                                            <TbCoin />
                                            Preço
                                        </FieldLabel>
                                        <FieldValue>
                                            {institute.ed_institute_licence_price != null
                                                ? `${institute.ed_institute_licence_price} ${institute.ed_institute_licence_coin || ""}`
                                                : "-"}
                                        </FieldValue>
                                    </Col>
                                    <Col xs={6} md={4}>
                                        <FieldLabel>
                                            <TbInfoCircle />
                                            Tipo de licença
                                        </FieldLabel>
                                        <FieldValue>{institute.ed_institute_licence_type || "-"}</FieldValue>
                                    </Col>
                                    <Col xs={6} md={4}>
                                        <FieldLabel>
                                            <TbId />
                                            Código da licença
                                        </FieldLabel>
                                        <input type="text" readOnly value={institute.ed_institute_licence_code || "-"} className="form-control" />
                                    </Col>
                                    <Col xs={6} md={4}>
                                        <FieldLabel>
                                            <TbUserCircle />
                                            Operador
                                        </FieldLabel>
                                        <FieldValue>{institute.ed_institute_licence_operator || "-"}</FieldValue>
                                    </Col>
                                </FieldGrid>

                                <ActionsRow>
                                    <div>
                                        <FormLabel htmlFor="slacm-amount">Dias</FormLabel>
                                        <AmountField
                                            id="slacm-amount"
                                            type="number"
                                            min={1}
                                            value={periodAmount}
                                            disabled={anyActionBusy}
                                            onChange={(e) => setPeriodAmount(e.target.value)}
                                        />
                                    </div>
                                    <PrimaryButton
                                        type="button"
                                        disabled={anyActionBusy}
                                        onClick={() => handleLicensePeriod("increment")}
                                    >
                                        {periodActionLoading === "increment" ? (
                                            <Spinner animation="border" size="sm" />
                                        ) : (
                                            <TbPlus />
                                        )}
                                        Incrementar
                                    </PrimaryButton>
                                    <GhostButton
                                        type="button"
                                        disabled={anyActionBusy}
                                        onClick={() => handleLicensePeriod("decrement")}
                                    >
                                        {periodActionLoading === "decrement" ? (
                                            <Spinner animation="border" size="sm" />
                                        ) : (
                                            <TbClockHour4 />
                                        )}
                                        Decrementar
                                    </GhostButton>
                                </ActionsRow>
                            </SectionCard>

                            {/* -------- Contactos -------- */}
                            <SectionCard $accent="contacts">
                                <SectionHead>
                                    <SectionTitle $iconColor={tokens.sage}>
                                        <TbPhone />
                                        Contactos
                                    </SectionTitle>
                                    <SuccessButton type="button" disabled={anyActionBusy} onClick={openNewContact}>
                                        <TbPlus />
                                        Novo contacto
                                    </SuccessButton>
                                </SectionHead>

                                {contactsLoading && (
                                    <LoadingBlock style={{ padding: "24px 16px" }}>
                                        <Spinner animation="border" size="sm" />
                                    </LoadingBlock>
                                )}

                                {!contactsLoading && contacts.length === 0 && (
                                    <EmptyState>
                                        <TbUsersGroup />
                                        <div>Ainda não existem contactos registados.</div>
                                    </EmptyState>
                                )}

                                {!contactsLoading && contacts.length > 0 && (
                                    <TableWrap>
                                        <StyledTable responsive hover className="align-middle mb-0">
                                            <thead>
                                                <tr>
                                                    <th>Nome</th>
                                                    <th>Cargo</th>
                                                    <th>Telefone</th>
                                                    <th className="text-end">Ações</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {contacts.map((contact, index) => {
                                                    const key = contact && contact.id != null ? contact.id : index;
                                                    return (
                                                        <tr key={key}>
                                                            <td>
                                                                <ContactName>
                                                                    <Avatar>{getInitials(contact && contact.nome)}</Avatar>
                                                                    {(contact && contact.nome) || "-"}
                                                                </ContactName>
                                                            </td>
                                                            <td>{(contact && contact.cargo) || "-"}</td>
                                                            <td>{(contact && contact.telefone) || "-"}</td>
                                                            <td className="text-end">
                                                                <EditGhostButton
                                                                    type="button"
                                                                    disabled={anyActionBusy}
                                                                    onClick={() => openEditContact(contact)}
                                                                    style={{ marginRight: 8 }}
                                                                >
                                                                    <TbEdit />
                                                                </EditGhostButton>
                                                                <DangerGhostButton
                                                                    type="button"
                                                                    disabled={anyActionBusy}
                                                                    onClick={() => handleDeleteContact(contact)}
                                                                >
                                                                    {deletingContactId === (contact && contact.id) ? (
                                                                        <Spinner animation="border" size="sm" />
                                                                    ) : (
                                                                        <TbTrash />
                                                                    )}
                                                                </DangerGhostButton>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </StyledTable>
                                    </TableWrap>
                                )}

                                {contactModalData && (
                                    <ContactFormWrap onSubmit={submitContact}>
                                        <Row className="g-2">
                                            <Col xs={12} md={4}>
                                                <Form.Group>
                                                    <FormLabel>Nome</FormLabel>
                                                    <StyledInput
                                                        value={contactForm.nome}
                                                        disabled={savingContact}
                                                        onChange={(e) => handleContactFieldChange("nome", e.target.value)}
                                                        required
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col xs={12} md={4}>
                                                <Form.Group>
                                                    <FormLabel>Cargo</FormLabel>
                                                    <StyledInput
                                                        value={contactForm.cargo}
                                                        disabled={savingContact}
                                                        onChange={(e) => handleContactFieldChange("cargo", e.target.value)}
                                                        required
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col xs={12} md={4}>
                                                <Form.Group>
                                                    <FormLabel>Telefone</FormLabel>
                                                    <StyledInput
                                                        value={contactForm.telefone}
                                                        disabled={savingContact}
                                                        onChange={(e) => handleContactFieldChange("telefone", e.target.value)}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <FormActions>
                                            <GhostButton type="button" disabled={savingContact} onClick={closeContactForm}>
                                                <TbX />
                                                Cancelar
                                            </GhostButton>
                                            <PrimaryButton type="submit" disabled={savingContact}>
                                                {savingContact ? <Spinner animation="border" size="sm" /> : <TbDeviceFloppy />}
                                                {contactModalData && contactModalData.id ? "Guardar" : "Criar"}
                                            </PrimaryButton>
                                        </FormActions>
                                    </ContactFormWrap>
                                )}
                            </SectionCard>
                        </>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <FooterBar>
                        <GhostButton type="button" onClick={handleClose} disabled={anyActionBusy}>
                            Fechar
                        </GhostButton>
                    </FooterBar>
                </Modal.Footer>
            </StyledModal>
        </div>
    );
}